import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Skeleton, Pagination, IconButton,
  LinearProgress, Stack, Menu, MenuItem, Chip, Link,
  ToggleButtonGroup, ToggleButton, Popover, useMediaQuery, useTheme,
  CircularProgress,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TuneIcon from '@mui/icons-material/Tune';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  getMyGallery, getPublicGallery, getGalleryQuota, deleteGalleryImage, getGalleryImage,
  GalleryImage, GalleryQuota,
} from '../../services/gallery_api';
import StyledButton from '../common/StyledButton';
import GalleryUploadDialog from './GalleryUploadDialog';
import GalleryEditDialog from './GalleryEditDialog';
import GalleryLightbox from './GalleryLightbox';
import GameSelectDialog, { GameSelectResult } from '../dialogs/GameSelectDialog';
import {
  SubscriptionPlan, GALLERY_COUNT_LIMITS, GALLERY_RESOLUTION_MAX, GALLERY_MONTHLY_UPLOAD_LIMITS,
} from '../../utils/subscription';
import { isDev } from '../../utils/env';
import GalleryQuotaDevTools from './GalleryQuotaDevTools';

interface Props {
  username?: string;
  isPublic?: boolean;
}

type DensityMode = 'comfortable' | 'compact';

// 統一トークン
const TILE_RADIUS = '10px';
const GAP = 8;               // タイル間ギャップ(px)
const DEFAULT_ASPECT = 16 / 9;

// 表示密度 → justified の目標行高(px)。狭い画面では縮小して適用する。
const TARGET_ROW_HEIGHT: Record<DensityMode, number> = {
  comfortable: 220,
  compact: 140,
};

const STORAGE_KEY_DENSITY = 'galleryDensity';
// ゲーム名の表示可否。既存ユーザーの選択を引き継ぐためキー文字列は据え置き（値は 'true'/'false'）。
const STORAGE_KEY_SHOW_GAME_NAME = 'galleryShowLabels';

// ───────────────────────── Justified レイアウト ─────────────────────────

interface JustifiedItem {
  image: GalleryImage;
  aspect: number;
}
interface JustifiedRow {
  items: JustifiedItem[];
  height: number;
  stretched: boolean;   // true: 横幅いっぱいに伸縮 / false: 自然高(最終行)
}

/** Flickr 方式の justified 行レイアウト計算 */
const computeJustifiedRows = (
  items: JustifiedItem[],
  containerWidth: number,
  targetHeight: number,
): JustifiedRow[] => {
  if (containerWidth <= 0 || items.length === 0) return [];
  const rows: JustifiedRow[] = [];
  let current: JustifiedItem[] = [];
  let aspectSum = 0;

  for (const it of items) {
    current.push(it);
    aspectSum += it.aspect;
    const naturalWidth = aspectSum * targetHeight + GAP * (current.length - 1);
    if (naturalWidth >= containerWidth) {
      const h = (containerWidth - GAP * (current.length - 1)) / aspectSum;
      rows.push({ items: current, height: h, stretched: true });
      current = [];
      aspectSum = 0;
    }
  }
  // 最終行（余り）は伸縮させず左寄せ。ただし高さは直前の行に揃えて不揃い感を消す。
  // 揃えた高さで横幅を超える場合のみ伸縮にフォールバック。
  if (current.length > 0) {
    const prevHeight = rows.length > 0 ? rows[rows.length - 1].height : targetHeight;
    const naturalWidth = aspectSum * prevHeight + GAP * (current.length - 1);
    if (naturalWidth >= containerWidth) {
      const h = (containerWidth - GAP * (current.length - 1)) / aspectSum;
      rows.push({ items: current, height: h, stretched: true });
    } else {
      rows.push({ items: current, height: prevHeight, stretched: false });
    }
  }
  return rows;
};

/** コンテナ幅を ResizeObserver で追跡 */
const useContainerWidth = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
};

// ───────────────────────── 容量インジケータ ─────────────────────────

const ProgressBar: React.FC<{
  label: string;
  used: number;
  limit: number;
  suffix?: string;
}> = ({ label, used, limit, suffix = '枚' }) => {
  const ratio = limit > 0 ? used / limit : 0;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="caption" color="text.secondary">
          {used.toLocaleString()} / {limit.toLocaleString()} {suffix}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, ratio * 100)}
        sx={{ height: 4, borderRadius: 2 }}
        color={ratio > 0.9 ? 'error' : ratio > 0.7 ? 'warning' : 'primary'}
      />
    </Box>
  );
};

const ratioColor = (ratio: number) =>
  ratio >= 0.9 ? 'error.main' : ratio >= 0.7 ? 'warning.main' : 'primary.main';

/** ツールバーに置く最小化された容量ボタン（クリックで詳細ポップオーバー）。
 *  文字/アイコンは常時ニュートラル、使用率は底辺の細いラインのみで表現（色のうるささを回避）。 */
const QuotaButton: React.FC<{ quota: GalleryQuota; onClick: (e: React.MouseEvent<HTMLElement>) => void }> = ({ quota, onClick }) => {
  const ratio = quota.count_limit > 0 ? quota.count_used / quota.count_limit : 0;
  return (
    <Box
      role="button"
      onClick={onClick}
      title="保存容量"
      sx={{
        position: 'relative', overflow: 'hidden',
        display: 'flex', alignItems: 'center', gap: 0.5,
        height: 32, px: 1, cursor: 'pointer',
        border: '1px solid', borderColor: 'divider', borderRadius: '8px',
        color: 'text.secondary', transition: 'border-color 0.2s',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <PhotoLibraryIcon sx={{ fontSize: 16 }} />
      <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
        {quota.count_used.toLocaleString()}/{quota.count_limit.toLocaleString()}
      </Typography>
      <Box sx={{
        position: 'absolute', left: 0, bottom: 0, height: 2,
        width: `${Math.min(100, ratio * 100)}%`,
        backgroundColor: ratioColor(ratio),
        transition: 'width 0.3s ease, background-color 0.3s ease',
      }} />
    </Box>
  );
};

const planName = (plan: number) =>
  plan >= SubscriptionPlan.MAX ? 'MAX' : plan >= SubscriptionPlan.PRO ? 'STANDARD' : 'FREE';

/** ギャラリー専用のプラン導線。上位ティアは「保存枚数・画質」＝プラン属性のみをベネフィットとして提示。
 *  月間アップロード上限は契約期間(月額のみ)に依存する“制約”なので、行内に混ぜず ※注記で分離する。
 *  MAXでは非表示。色でうるさくしない設計。 */
const GalleryPlanUpsell: React.FC<{ plan: number }> = ({ plan }) => {
  if (plan >= SubscriptionPlan.MAX) return null;
  const tiers: Array<'standard' | 'max'> = plan < SubscriptionPlan.PRO ? ['standard', 'max'] : ['max'];
  const tierLabel = { standard: 'STANDARD', max: 'MAX' } as const;
  return (
    <Box sx={{
      mt: 0.5, p: 1.25, borderRadius: '8px',
      border: '1px solid', borderColor: 'rgba(255,255,255,0.08)',
      backgroundColor: 'rgba(255,255,255,0.02)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
        <AutoAwesomeIcon sx={{ fontSize: 14, color: 'primary.main' }} />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>PROでもっと便利に</Typography>
      </Box>
      <Stack spacing={0.5}>
        {tiers.map((t) => (
          <Box key={t} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 62, flexShrink: 0 }}>
              {tierLabel[t]}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.5 }}>
              {GALLERY_COUNT_LIMITS[t].toLocaleString()}枚 · 高画質/{GALLERY_RESOLUTION_MAX[t]}px
            </Typography>
          </Box>
        ))}
      </Stack>

      <Link
        component="button"
        type="button"
        onClick={() => window.open('/pricing', '_blank', 'noopener,noreferrer')}
        sx={{
          display: 'inline-block', mt: 0.75, fontSize: '0.72rem',
          color: 'primary.main', fontWeight: 600, textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        プランを見る →
      </Link>

      {/* 月間上限は「月額契約のみ」の制約。ベネフィット行と混同させないため注記で分離 */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 0.75, fontSize: '0.62rem', lineHeight: 1.5 }}
      >
        ※月額契約の場合、保存上限とは別に直近30日のアップロード上限があります
        （STANDARD {GALLERY_MONTHLY_UPLOAD_LIMITS.standard}枚・MAX {GALLERY_MONTHLY_UPLOAD_LIMITS.max}枚）。年額は無制限。
      </Typography>
    </Box>
  );
};

const QuotaPopover: React.FC<{
  anchor: HTMLElement | null;
  quota: GalleryQuota;
  onClose: () => void;
}> = ({ anchor, quota, onClose }) => {
  const hasMonthly = quota.monthly_upload_limit != null && quota.monthly_upload_used != null;
  const isPaid = quota.plan >= SubscriptionPlan.PRO;
  return (
    <Popover
      open={!!anchor}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ sx: { p: 2, minWidth: 280, maxWidth: 320 } }}
    >
      <Stack spacing={1.5}>
        {/* 現在プラン */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">プラン</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {planName(quota.plan)}
            {quota.billing_period ? `・${quota.billing_period === 'yearly' ? '年額' : '月額'}` : ''}
          </Typography>
        </Box>

        <ProgressBar label="保存枚数" used={quota.count_used} limit={quota.count_limit} />

        {/* 月アップロード: 月額=バー / 年額など有料の非月額=無制限 / FREE=非表示 */}
        {hasMonthly ? (
          <ProgressBar
            label="直近30日のアップロード"
            used={quota.monthly_upload_used!}
            limit={quota.monthly_upload_limit!}
          />
        ) : isPaid ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">直近30日のアップロード</Typography>
            <Typography variant="caption" color="text.secondary">無制限</Typography>
          </Box>
        ) : null}

        <GalleryPlanUpsell plan={quota.plan} />
      </Stack>
    </Popover>
  );
};

// ───────────────────────── タイル ─────────────────────────

const CornerBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{
    backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
    borderRadius: '50%', width: 24, height: 24,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    {children}
  </Box>
);

const TopLeftBadges: React.FC<{ isSpoiler: boolean }> = ({ isSpoiler }) => {
  if (!isSpoiler) return null;
  return (
    <Box sx={{
      position: 'absolute', top: 8, left: 8, zIndex: 2,
      display: 'flex', gap: 0.5,
    }}>
      <CornerBadge>
        <VisibilityOffIcon sx={{ fontSize: 14, color: '#fff' }} />
      </CornerBadge>
    </Box>
  );
};

const GalleryImageTile: React.FC<{
  image: GalleryImage;
  showGameName: boolean;
  onClick: () => void;
  onMenu?: (anchor: HTMLElement, image: GalleryImage) => void;
  isOwner: boolean;
}> = ({ image, showGameName, onClick, onMenu, isOwner }) => {
  const [spoilerRevealed, setSpoilerRevealed] = useState(false);
  // オーナーは常に自分の画像を見られる（blur対象外）。他人のみネタバレblur適用
  const showBlur = image.is_spoiler && !spoilerRevealed && !isOwner;
  const isProcessing = image.status === 0;
  const navigate = useNavigate();
  const totalReactions = image.reaction_counts
    ? Object.values(image.reaction_counts).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%', height: '100%',
        backgroundColor: 'rgba(255,255,255,0.04)',
        borderRadius: TILE_RADIUS,
        overflow: 'hidden',
        cursor: isProcessing ? 'default' : 'pointer',
        '&:hover': isProcessing ? {} : {
          '& .gallery-img': { transform: 'scale(1.05)' },
          '& .gallery-bottom-gradient': { opacity: 1 },
          '& .gallery-owner-menu': { opacity: 1 },
          '& .gallery-ring': { opacity: 1 },
        },
      }}
      onClick={() => {
        if (isProcessing) return;
        if (showBlur) { setSpoilerRevealed(true); return; }
        onClick();
      }}
    >
      {isProcessing ? (
        <>
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          />
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 1,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.75,
          }}>
            <CircularProgress size={22} thickness={4} sx={{ color: 'rgba(255,255,255,0.6)' }} />
            <Typography variant="caption" color="text.secondary">処理中...</Typography>
          </Box>
        </>
      ) : image.thumb_url ? (
        <Box
          className="gallery-img"
          component="img"
          src={image.thumb_url}
          alt={image.caption || ''}
          loading="lazy"
          sx={{
            width: '100%', height: '100%', objectFit: 'cover',
            filter: showBlur ? 'blur(24px)' : 'none',
            transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1), filter 0.3s',
            display: 'block',
          }}
        />
      ) : null}

      {/* ホバー時に浮かぶ内側リング（浮き上がりの代替・隣接タイルと干渉しない） */}
      {!isProcessing && (
        <Box
          className="gallery-ring"
          sx={{
            position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none',
            borderRadius: TILE_RADIUS,
            boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.25)',
            opacity: 0, transition: 'opacity 0.2s ease',
          }}
        />
      )}

      {/* 左上バッジ：公開範囲＋ネタバレ印（オーナーも含めて常時表示） */}
      <TopLeftBadges isSpoiler={image.is_spoiler} />

      {/* 他人のネタバレ画像のみ薄い暗幕＋「タップで表示」ヒント。画像自体は filter:blur で霞んでいる */}
      {showBlur && (
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 1,
          backgroundColor: 'rgba(0,0,0,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            px: 1.25, py: 0.5, borderRadius: '999px',
            backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            color: 'rgba(255,255,255,0.9)',
          }}>
            <VisibilityOffIcon sx={{ fontSize: 14 }} />
            <Typography variant="caption" sx={{ fontSize: '0.68rem', lineHeight: 1 }}>タップで表示</Typography>
          </Box>
        </Box>
      )}

      {/* 下部ラベル：リアクション数(▲N)は常時、ゲーム名は設定でON時のみ（デフォOFF）。
          ネタバレぼかし時もゲーム名は表示（見る側が「見るか」判断できるように）。 */}
      {!isProcessing && (totalReactions > 0 || (showGameName && image.game)) && (
        <Box
          className="gallery-bottom-gradient"
          sx={{
            position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 2,
            background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.25) 70%, rgba(0,0,0,0) 100%)',
            px: 1, pb: 0.5, pt: 2.5,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 0.75,
            opacity: 0.8,
            transition: 'opacity 0.25s ease',
            pointerEvents: 'none',
          }}
        >
          {showGameName && image.game ? (
            <Typography
              onClick={(e) => { e.stopPropagation(); navigate(`/game/${image.game!.id}`); }}
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.92)', fontSize: {xs: '0.5rem', sm: '0.6rem'}, fontWeight: 500,
                textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                minWidth: 0, pointerEvents: 'auto', cursor: 'pointer',
                '&:hover': { color: '#fff', textDecoration: 'underline' },
              }}
            >
              {image.game.name}
            </Typography>
          ) : <Box />}

          {totalReactions > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.92)', fontSize: {xs: '0.5rem', sm: '0.6rem'}, fontWeight: 500,
                textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              ▲{totalReactions}
            </Typography>
          )}
        </Box>
      )}

      {isOwner && onMenu && !isProcessing && (
        <Box
          className="gallery-owner-menu"
          sx={{
            position: 'absolute', top: 6, right: 6, zIndex: 4,
            opacity: 0, transition: 'opacity 0.2s',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            size="small"
            onClick={(e) => onMenu(e.currentTarget, image)}
            sx={{
              color: '#fff',
              backgroundColor: 'rgba(0,0,0,0.55)',
              backdropFilter: 'blur(4px)',
              width: 28, height: 28,
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
            }}
          >
            <MoreVertIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

// ───────────────────────── 表示設定ポップオーバー ─────────────────────────

const ViewSettingsPopover: React.FC<{
  anchor: HTMLElement | null;
  onClose: () => void;
  density: DensityMode;
  showGameName: boolean;
  onDensityChange: (v: DensityMode) => void;
  onShowGameNameChange: (v: boolean) => void;
}> = ({ anchor, onClose, density, showGameName, onDensityChange, onShowGameNameChange }) => (
  <Popover
    open={!!anchor}
    anchorEl={anchor}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    PaperProps={{ sx: { p: 2, minWidth: 240 } }}
  >
    <Stack spacing={2}>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          表示サイズ
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={density}
          onChange={(_, v) => v && onDensityChange(v)}
          fullWidth
        >
          <ToggleButton value="comfortable" sx={{ fontSize: '0.7rem' }}>大きく</ToggleButton>
          <ToggleButton value="compact" sx={{ fontSize: '0.7rem' }}>たくさん</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          ゲーム名
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={showGameName ? 'show' : 'hide'}
          onChange={(_, v) => v && onShowGameNameChange(v === 'show')}
          fullWidth
        >
          <ToggleButton value="show" sx={{ fontSize: '0.7rem' }}>表示</ToggleButton>
          <ToggleButton value="hide" sx={{ fontSize: '0.7rem' }}>非表示</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Stack>
  </Popover>
);

// 読み込み中スケルトン（justified風の行）
const SkeletonRows: React.FC<{ rowHeight: number }> = ({ rowHeight }) => {
  const pattern = [[3, 2, 2, 3], [2, 3, 3], [3, 2, 2, 2]];
  return (
    <>
      {pattern.map((row, ri) => (
        <Box key={ri} sx={{ display: 'flex', gap: `${GAP}px`, mb: `${GAP}px` }}>
          {row.map((w, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              sx={{ flexGrow: w, flexBasis: 0, height: rowHeight, borderRadius: TILE_RADIUS }}
            />
          ))}
        </Box>
      ))}
    </>
  );
};

// ───────────────────────── メイン ─────────────────────────

const GalleryTab: React.FC<Props> = ({ username, isPublic = false }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuImage, setMenuImage] = useState<GalleryImage | null>(null);
  const [editImage, setEditImage] = useState<GalleryImage | null>(null);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);
  const [quotaAnchor, setQuotaAnchor] = useState<HTMLElement | null>(null);
  const [gameFilter, setGameFilter] = useState<GameSelectResult | null>(null);
  const [gameSelectOpen, setGameSelectOpen] = useState(false);

  const [density, setDensity] = useState<DensityMode>(() =>
    (localStorage.getItem(STORAGE_KEY_DENSITY) as DensityMode) || 'comfortable'
  );
  const [showGameName, setShowGameName] = useState<boolean>(() =>
    localStorage.getItem(STORAGE_KEY_SHOW_GAME_NAME) === 'true'  // デフォルトOFF（ギャラリーは画像ファースト）
  );

  useEffect(() => { localStorage.setItem(STORAGE_KEY_DENSITY, density); }, [density]);
  useEffect(() => { localStorage.setItem(STORAGE_KEY_SHOW_GAME_NAME, String(showGameName)); }, [showGameName]);

  // ゲームフィルター変更時はページを1に戻す
  useEffect(() => { setPage(1); }, [gameFilter?.game_id]);

  const stableKey = isPublic ? username : 'me';
  const gameId = gameFilter?.game_id;

  const { data: galleryData, isLoading } = useQuery({
    queryKey: ['gallery', stableKey, page, gameId],
    queryFn: () => isPublic
      ? getPublicGallery(username!, page, gameId)
      : getMyGallery(page, gameId),
    enabled: isPublic ? !!username : true,
    staleTime: isPublic ? 30000 : 2000,
    refetchInterval: (query) => {
      const data = query.state.data as any;
      const hasProcessing = data?.results?.some((i: GalleryImage) => i.status === 0);
      return hasProcessing ? 3000 : false;
    },
  });

  const { data: quota } = useQuery({
    queryKey: ['gallery-quota'],
    queryFn: getGalleryQuota,
    enabled: !isPublic,
    staleTime: 10000,
  });

  // 【DEV専用・削除可】保存枚数の偽装。本番は isDev() ゲートで UI が出ず、常に null。
  const [devFakeCount, setDevFakeCount] = useState<number | null>(null);
  const effectiveQuota = quota && devFakeCount != null
    ? { ...quota, count_used: devFakeCount }
    : quota;

  const images: GalleryImage[] = galleryData?.results || [];
  const totalCount = galleryData?.count || 0;
  const totalPages = Math.ceil(totalCount / 24);

  // 通知からのディープリンク（?image=<id>）: その画像だけをライトボックスで開く
  const deepLinkImageId = searchParams.get('image');
  const [deepLinkImage, setDeepLinkImage] = useState<GalleryImage | null>(null);
  useEffect(() => {
    if (!deepLinkImageId || isPublic) return;
    let cancelled = false;
    getGalleryImage(Number(deepLinkImageId))
      .then((img) => {
        if (cancelled) return;
        setDeepLinkImage(img);
        setLightboxIndex(0);
      })
      .catch(() => { /* 削除済み等は無視 */ });
    return () => { cancelled = true; };
  }, [deepLinkImageId, isPublic]);

  // ディープリンク中は対象1枚のみを対象にする（通常時はページ内の画像）
  const lightboxImages = deepLinkImage ? [deepLinkImage] : images;

  const closeLightbox = () => {
    setLightboxIndex(null);
    if (deepLinkImage) {
      setDeepLinkImage(null);
      const next = new URLSearchParams(searchParams);
      next.delete('image');
      setSearchParams(next, { replace: true });
    }
  };

  // ライトボックスのリアクション更新をクエリキャッシュへ反映（開いたまま最新カウント表示）
  const handleLightboxReaction = (
    imageId: number,
    reactions: { [key: number]: number },
    userReaction: number | null,
  ) => {
    setDeepLinkImage((prev) =>
      prev && prev.id === imageId ? { ...prev, reaction_counts: reactions, user_reaction: userReaction } : prev
    );
    queryClient.setQueryData(['gallery', stableKey, page, gameId], (old: any) => {
      if (!old?.results) return old;
      return {
        ...old,
        results: old.results.map((im: GalleryImage) =>
          im.id === imageId ? { ...im, reaction_counts: reactions, user_reaction: userReaction } : im
        ),
      };
    });
  };

  // justified レイアウト計算
  const { ref: gridRef, width: containerWidth } = useContainerWidth();
  const baseTarget = TARGET_ROW_HEIGHT[density];
  // 狭い画面では行高を縮小（モバイルで1枚/行になりすぎないように）
  const targetHeight = containerWidth > 0 && containerWidth < 600
    ? Math.round(baseTarget * 0.7)
    : baseTarget;

  const rows = useMemo(() => {
    const items: JustifiedItem[] = images.map((image) => ({
      image,
      aspect: image.width && image.height ? image.width / image.height : DEFAULT_ASPECT,
    }));
    return computeJustifiedRows(items, containerWidth, targetHeight);
  }, [images, containerWidth, targetHeight]);

  const invalidateGallery = () => {
    queryClient.invalidateQueries({ queryKey: ['gallery', stableKey] });
    queryClient.invalidateQueries({ queryKey: ['gallery-quota'] });
  };

  /** 確認 → 削除 → 一覧/クォータ再取得。削除できたら true。タイルメニューとライトボックスで共用。 */
  const confirmAndDelete = async (image: GalleryImage): Promise<boolean> => {
    if (!window.confirm('この画像を削除しますか？')) return false;
    try {
      await deleteGalleryImage(image.id);
      invalidateGallery();
      return true;
    } catch (e) {
      console.error('削除に失敗しました', e);
      return false;
    }
  };

  const handleDelete = async () => {
    if (!menuImage) return;
    const target = menuImage;
    setMenuAnchor(null);
    await confirmAndDelete(target);
    setMenuImage(null);
  };

  // ライトボックスから削除したら、対象が消えるのでライトボックスを閉じる
  const handleLightboxDelete = async (image: GalleryImage) => {
    if (await confirmAndDelete(image)) closeLightbox();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        {!isPublic ? (
          <StyledButton onClick={() => setUploadOpen(true)} startIcon={<AddPhotoAlternateIcon />}>
            画像をアップロード
          </StyledButton>
        ) : <Box />}

        {/* 右側ツールアイコン群（容量 + フィルター + 表示設定） */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {!isPublic && effectiveQuota && (
            <QuotaButton quota={effectiveQuota} onClick={(e) => setQuotaAnchor(e.currentTarget)} />
          )}
          {gameFilter ? (
            <Chip
              icon={<SportsEsportsIcon sx={{ fontSize: 14 }} />}
              label={gameFilter.japanese_name || gameFilter.name}
              onDelete={() => setGameFilter(null)}
              onClick={() => setGameSelectOpen(true)}
              size="small"
              sx={{
                maxWidth: 220, height: 32,
                backgroundColor: 'rgba(32, 136, 255, 0.12)',
                color: 'primary.main',
                border: '1px solid',
                borderColor: 'primary.main',
                '& .MuiChip-icon': { color: 'primary.main' },
              }}
            />
          ) : (
            <IconButton
              size="small"
              onClick={() => setGameSelectOpen(true)}
              title="ゲームで絞り込み"
              sx={{
                color: 'text.secondary',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                width: 32, height: 32,
                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
              }}
            >
              <SportsEsportsIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={(e) => setSettingsAnchor(e.currentTarget)}
            title="表示設定"
            sx={{
              color: 'text.secondary',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              width: 32, height: 32,
              '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
            }}
          >
            <TuneIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* 【DEV専用・削除可】枚数偽装ツール。本番は isDev() で描画されない。 */}
      {isDev() && !isPublic && effectiveQuota && (
        <GalleryQuotaDevTools quota={effectiveQuota} value={devFakeCount} onChange={setDevFakeCount} />
      )}

      <Box ref={gridRef}>
        {isLoading ? (
          <SkeletonRows rowHeight={targetHeight} />
        ) : images.length === 0 ? (
          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', py: 10, px: 2, gap: 2,
          }}>
            <Box sx={{
              width: 72, height: 72, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid', borderColor: 'divider',
            }}>
              <PhotoLibraryIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
              {isPublic
                ? 'まだ画像がありません。'
                : gameFilter
                  ? 'このゲームの画像はまだありません。'
                  : 'お気に入りのスクリーンショットをアップロードして、あなたのギャラリーを作りましょう。'}
            </Typography>
          </Box>
        ) : (
          rows.map((row, ri) => (
            <Box
              key={ri}
              sx={{
                display: 'flex',
                gap: `${GAP}px`,
                mb: `${GAP}px`,
                justifyContent: 'flex-start',
              }}
            >
              {row.items.map((it) => (
                <Box
                  key={it.image.id}
                  sx={row.stretched
                    ? { flexGrow: it.aspect, flexBasis: 0, height: row.height, minWidth: 0 }
                    : { width: it.aspect * row.height, height: row.height, flexShrink: 0 }
                  }
                >
                  <GalleryImageTile
                    image={it.image}
                    showGameName={showGameName}
                    isOwner={!isPublic}
                    onClick={() => {
                      setDeepLinkImage(null);  // 通常閲覧に戻す
                      setLightboxIndex(images.findIndex((im) => im.id === it.image.id));
                    }}
                    onMenu={!isPublic ? (anchor, img) => { setMenuAnchor(anchor); setMenuImage(img); } : undefined}
                  />
                </Box>
              ))}
            </Box>
          ))
        )}
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} />
        </Box>
      )}

      {effectiveQuota && (
        <QuotaPopover
          anchor={quotaAnchor}
          quota={effectiveQuota}
          onClose={() => setQuotaAnchor(null)}
        />
      )}

      <ViewSettingsPopover
        anchor={settingsAnchor}
        onClose={() => setSettingsAnchor(null)}
        density={density}
        showGameName={showGameName}
        onDensityChange={setDensity}
        onShowGameNameChange={setShowGameName}
      />

      <GalleryUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => invalidateGallery()}
        quota={quota || null}
      />

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            if (menuImage) setEditImage(menuImage);
            setMenuAnchor(null);
          }}
        >
          編集
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>削除</MenuItem>
      </Menu>

      <GalleryEditDialog
        open={!!editImage}
        image={editImage}
        onClose={() => setEditImage(null)}
        onUpdated={() => {
          // 一覧キャッシュ更新（開いているライトボックスも images 経由で反映される）
          invalidateGallery();
        }}
      />

      <GameSelectDialog
        open={gameSelectOpen}
        onClose={() => setGameSelectOpen(false)}
        onSelect={(g) => { setGameFilter(g); setGameSelectOpen(false); }}
        title="ゲームで絞り込み"
      />

      <GalleryLightbox
        images={lightboxImages}
        index={lightboxIndex}
        onClose={closeLightbox}
        onNavigate={setLightboxIndex}
        isPublic={isPublic}
        onEdit={!isPublic ? (img) => setEditImage(img) : undefined}
        onDelete={!isPublic ? handleLightboxDelete : undefined}
        onReactionUpdate={handleLightboxReaction}
      />
    </Box>
  );
};

export default GalleryTab;
